/**
 * helpStore — drives the three Vibe Designer help overlays (2026-06-27):
 *   1. guided first-run TOUR (spotlight walks the anchors in helpContent.walkthrough)
 *   2. toggleable HELP MODE (persistent callouts + rich hover cards on every anchor)
 *   3. (rich hover cards live inside help mode — hovering an anchor shows its card)
 *
 * All COPY lives in config (helpContent.json); this store holds only UI STATE.
 * `hasSeenTour` persists in localStorage so the tour auto-runs only once.
 */
import { create } from 'zustand';
import { walkthrough } from '@/config/helpContent';

const SEEN_KEY = 'vibe_help_seen_tour';

function readSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // ignore (private mode / disabled storage) — tour just re-runs next time.
  }
}

interface HelpState {
  /** Persistent callouts + hover cards visible on every anchor. */
  helpMode: boolean;
  /** Active tour step index, or null when the tour is not running. */
  tourStep: number | null;
  toggleHelpMode: () => void;
  setHelpMode: (on: boolean) => void;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  /** End the tour. Always marks the tour as seen so it won't auto-run again. */
  endTour: () => void;
  /** True once the user has seen (or dismissed) the tour. */
  hasSeenTour: () => boolean;
}

export const useHelpStore = create<HelpState>((set, get) => ({
  helpMode: false,
  tourStep: null,

  toggleHelpMode: () => set((s) => ({ helpMode: !s.helpMode })),
  setHelpMode: (on) => set({ helpMode: on }),

  startTour: () => set({ tourStep: 0, helpMode: false }),

  nextStep: () => {
    const cur = get().tourStep;
    if (cur === null) return;
    const next = cur + 1;
    if (next >= walkthrough.length) {
      writeSeen();
      set({ tourStep: null });
      return;
    }
    set({ tourStep: next });
  },

  prevStep: () => {
    const cur = get().tourStep;
    if (cur === null) return;
    set({ tourStep: Math.max(0, cur - 1) });
  },

  endTour: () => {
    writeSeen();
    set({ tourStep: null });
  },

  hasSeenTour: () => readSeen(),
}));
